// CSV download flow for the fiat-transactions list. Owns the mutation,
// the toast surface, and the "limit exceeded" error narrowing — so the
// container stays focused on the list shape itself.

import { toValue, type MaybeRefOrGetter } from 'vue'

import { useAppToast } from '@shared/composables/useAppToast'

import { useDownloadFiatTransactionsMutation } from '../api/useDownloadFiatTransactionsMutation'
import type { GetFiatTransactionsParams } from '../types/fiatTransactionsTypes'

const DOWNLOAD_LIMIT_CODE = 'FIAT_TRANSACTIONS_DOWNLOAD_LIMIT_EXCEEDED'

function describeError(err: unknown): string {
  if (!err) return ''
  if (err instanceof Error) return err.message
  const candidate = err as { message?: unknown; code?: unknown }
  if (typeof candidate.message === 'string') return candidate.message
  if (typeof candidate.code === 'string') return candidate.code
  return 'Something went wrong. Please try again.'
}

/**
 * Returns the download trigger + pending flag.
 *
 * Pass the same `listParams` the list query reads so the download applies
 * the operator's current filter selection. Pagination params are dropped
 * before the request because the endpoint streams every matching row.
 */
export function useFiatTransactionsDownload(
  listParams: MaybeRefOrGetter<GetFiatTransactionsParams>,
) {
  const { addToast } = useAppToast()
  const { mutate, isPending: isDownloading } = useDownloadFiatTransactionsMutation()

  function handleDownload() {
    const params: GetFiatTransactionsParams = { ...toValue(listParams) }
    delete params.page
    delete params.limit
    mutate(params, {
      onSuccess: () => {
        addToast({
          type: 'success',
          title: 'Download ready',
          message: 'Your CSV is being saved by the browser.',
        })
      },
      onError: (err) => {
        const message = describeError(err)
        const isLimitExceeded =
          message.includes(DOWNLOAD_LIMIT_CODE) ||
          (err as { code?: string })?.code === DOWNLOAD_LIMIT_CODE
        addToast({
          type: 'error',
          title: 'Download failed',
          message: isLimitExceeded
            ? 'Too many rows. Narrow filters and try again.'
            : message || 'Could not download transactions.',
        })
      },
    })
  }

  return { isDownloading, handleDownload }
}
